import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';

export const EMPTY_PRODUCT_ITEM = { productId: '', quantity: 1, totalCost: '', variantCombo: {} };
export const EMPTY_RM_ITEM      = { rawMaterialId: '', quantity: '', totalCost: '' };
export const EMPTY_FORM = { sourcerId: '', type: 'PRODUCT', notes: '', manualDiscount: 0, items: [{ ...EMPTY_PRODUCT_ITEM }] };

// Fields active for a product: its own fields, with the vendor-wide Common Structure filling in
// anything it doesn't define itself (a product field of the same name overrides the common one).
export function mergedFieldsFor(product, globalFields) {
  if (!product) return [];
  const ownFields = product.fields || [];
  const seen = new Set(ownFields.map(f => f.key));
  return [...globalFields.filter(f => !seen.has(f.key)), ...ownFields];
}

export const buildSourcingPayload = (form) => ({
  sourcerId: parseInt(form.sourcerId),
  type: form.type,
  notes: form.notes || null,
  manualDiscount: parseFloat(form.manualDiscount) || 0,
  items: form.items.map((i) => ({
    productId:      form.type === 'PRODUCT'       ? parseInt(i.productId)      : null,
    rawMaterialId:  form.type === 'RAW_MATERIAL'  ? parseInt(i.rawMaterialId)  : null,
    quantity:       parseFloat(i.quantity),
    unitCost:       parseFloat(i.totalCost) / Math.max(parseFloat(i.quantity), 0.001),
    variantCombo:   (form.type === 'PRODUCT' && i.variantCombo && Object.keys(i.variantCombo).length > 0)
      ? i.variantCombo : null,
  })),
});

export function SourcingOrderForm({ mode, initialForm, sourcers, products, rawMaterials, globalFields, onSubmit, onClose, isPending, error, lockType = false, hideSourcer = false, title, submitLabel, notice }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(initialForm);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setType = (type) => setForm((f) => ({
    ...f,
    type,
    items: [type === 'RAW_MATERIAL' ? { ...EMPTY_RM_ITEM } : { ...EMPTY_PRODUCT_ITEM }],
  }));

  const addItem = () => setForm((f) => ({
    ...f,
    items: [...f.items, f.type === 'RAW_MATERIAL' ? { ...EMPTY_RM_ITEM } : { ...EMPTY_PRODUCT_ITEM }],
  }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const setItem = (idx, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [k]: v } : it) }));

  // Changing the product must also clear any combo selection made for the previous product.
  const setItemProduct = (idx, productId) => setForm((f) => ({
    ...f, items: f.items.map((it, i) => i === idx ? { ...it, productId, variantCombo: {} } : it),
  }));

  const setItemCombo = (idx, key, value) => setForm((f) => ({
    ...f, items: f.items.map((it, i) => i === idx ? { ...it, variantCombo: { ...it.variantCombo, [key]: value } } : it),
  }));

  const isRm = form.type === 'RAW_MATERIAL';

  return (
    <Modal title={title ?? (mode === 'create' ? t('sourcingOrders.new') : `${t('common.edit')} ${t('sourcingOrders.title')}`)} onClose={onClose} wide>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="space-y-5">
        {notice && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{notice}</p>
        )}

        {/* Type toggle */}
        {!lockType && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.type')} <span className="text-red-500">*</span></label>
            <div className="flex gap-2">
              {['PRODUCT', 'RAW_MATERIAL'].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setType(type)}
                  disabled={mode === 'edit'}
                  className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    form.type === type
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                  } disabled:opacity-60 disabled:cursor-not-allowed`}
                >
                  {type === 'PRODUCT' ? t('sourcingOrders.typeProduct') : t('sourcingOrders.typeRawMaterial')}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Hidden in Simple view; the preselected default sourcer is still submitted */}
        {!hideSourcer && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('sourcers.title')} <span className="text-red-500">*</span></label>
            <select value={form.sourcerId} onChange={(e) => setField('sourcerId', e.target.value)} required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
              <option value="">{t('sourcingOrders.selectSourcer')}</option>
              {sourcers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}

        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">{t('common.items')}</p>
          <div className="space-y-2">
            {form.items.map((item, idx) => {
              const rm = isRm ? rawMaterials.find(r => String(r.id) === String(item.rawMaterialId)) : null;
              const product = isRm ? null : products.find(p => String(p.id) === String(item.productId));
              const comboFields = isRm ? [] : mergedFieldsFor(product, globalFields);
              return (
                <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                  <div className="flex items-center gap-2">
                    {isRm ? (
                      <select value={item.rawMaterialId} onChange={(e) => setItem(idx, 'rawMaterialId', e.target.value)} required
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                        <option value="">{t('sourcingOrders.selectRawMaterial')}</option>
                        {rawMaterials.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.unit})</option>)}
                      </select>
                    ) : (
                      <select value={item.productId} onChange={(e) => setItemProduct(idx, e.target.value)} required
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                        <option value="">{t('sourcingOrders.selectProduct')}</option>
                        {products.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                      </select>
                    )}
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => setItem(idx, 'quantity', e.target.value)}
                      min={isRm ? '0.001' : '1'}
                      step={isRm ? 'any' : '1'}
                      required
                      placeholder={t('common.qty')}
                      className="w-20 px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                    />
                    {isRm && rm && (
                      <span className="text-xs text-gray-400 w-8 shrink-0">{rm.unit}</span>
                    )}
                    <input type="number" value={item.totalCost} onChange={(e) => setItem(idx, 'totalCost', e.target.value)}
                      min="0" step="0.01" required placeholder={t('sourcingOrders.totalCostSymbol')}
                      className="w-28 px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    {form.items.length > 1 && (
                      <button type="button" onClick={() => removeItem(idx)} className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {comboFields.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2 pl-2 border-l-2 border-gray-200">
                      {comboFields.map(f => (
                        <div key={f.key} className="w-28">
                          <label className="block text-xs text-gray-400 mb-0.5">{f.key}</label>
                          {(f.options || []).length > 0 ? (
                            <select
                              value={item.variantCombo?.[f.key] || ''}
                              onChange={(e) => setItemCombo(idx, f.key, e.target.value)}
                              required
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                              <option value="">—</option>
                              {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={item.variantCombo?.[f.key] || ''}
                              onChange={(e) => setItemCombo(idx, f.key, e.target.value)}
                              placeholder={f.key}
                              required
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button type="button" onClick={addItem} className="mt-2 flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium">
            <Plus size={14} /> {t('sourcingOrders.addItem')}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('sourcingOrders.manualDiscount')}</label>
            <input type="number" value={form.manualDiscount} onChange={(e) => setField('manualDiscount', e.target.value)}
              min="0" step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0.00" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.notes')}</label>
            <input type="text" value={form.notes} onChange={(e) => setField('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('common.optional')} />
          </div>
        </div>

        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
          <button type="submit" disabled={isPending} className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
            {isPending ? t('common.saving') : (submitLabel ?? t('sourcingOrders.saveOrder'))}
          </button>
        </div>
      </form>
    </Modal>
  );
}
